import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import { positionErrorCode, positionErrorTranslationKey } from '../position-error';

export const PositionMessage: FunctionComponent<{
    readonly action?: ReactNode;
    readonly className?: string;
    readonly icon?: ReactNode;
    readonly message: string;
    readonly role?: 'alert';
}> = ({ action, className, icon, message, role }) => (
    <div className={clsx('flex w-full flex-col items-center gap-m py-xl', className)} role={role}>
        <div className="w-full">
            <EmptyStateMessage icon={icon}>{message}</EmptyStateMessage>
        </div>
        {action}
    </div>
);

export const PositionRetryButton: FunctionComponent<{
    readonly isRetrying: boolean;
    readonly onRetry: () => void;
}> = ({ isRetrying, onRetry }) => {
    const translate = useTranslate();

    return (
        <Button disabled={isRetrying} size="sm" variant="secondary" onClick={onRetry}>
            {translate('pages.position-management.retry')}
        </Button>
    );
};

export const PositionRetryMessage: FunctionComponent<{
    readonly className?: string;
    readonly isRetrying: boolean;
    readonly message: string;
    readonly onRetry: () => void;
}> = ({ className, isRetrying, message, onRetry }) => (
    <PositionMessage
        action={<PositionRetryButton isRetrying={isRetrying} onRetry={onRetry} />}
        className={className}
        message={message}
        role="alert"
    />
);

export const PositionNotFoundMessage: FunctionComponent<{
    readonly error: unknown;
    readonly onClose: () => void;
}> = ({ error, onClose }) => {
    const translate = useTranslate();

    return (
        <PositionMessage
            action={
                <Button size="sm" variant="secondary" onClick={onClose}>
                    {translate('common.close')}
                </Button>
            }
            message={translate(
                positionErrorCode(error) === ''
                    ? 'pages.position-management.errors.position-not-found'
                    : positionErrorTranslationKey(error),
            )}
            role="alert"
        />
    );
};

export const PositionLoading: FunctionComponent<{
    readonly className: string;
    readonly label: string;
}> = ({ className, label }) => (
    <div aria-busy={true} aria-label={label} className={clsx('flex', className)} role="status">
        <SkeletonBase className="h-full w-full rounded-xl" />
    </div>
);
